import React, { useState, useEffect, useCallback } from 'react';
import { X, Zap, Edit3, Users, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';

interface Term {
  id: number;
  name?: string;
  term?: string;
  definition: string;
  category?: string;
  confidence_score: number;
  project_id?: number;
}

interface MergedResult {
  name: string;
  definition: string;
  category?: string;
  confidence_score: number;
  merge_reasoning: string;
  merged_from: Array<{
    id: number;
    name: string;
    definition: string;
  }>;
}

interface MergePreview {
  preview: MergedResult;
  strategy_used: 'ai' | 'manual';
  terms_count: number;
  will_hide_terms: number[];
}

interface MergeTermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedTerms: Term[];
  projectId: number;
  onMergeComplete: () => void;
}

const MergeTermsModal: React.FC<MergeTermsModalProps> = ({
  isOpen,
  onClose,
  selectedTerms,
  projectId,
  onMergeComplete
}) => {
  const [mergeStrategy, setMergeStrategy] = useState<'ai' | 'manual'>('ai');
  const [primaryTermId, setPrimaryTermId] = useState<number | null>(null);
  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [editedResult, setEditedResult] = useState<MergedResult | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setPreview(null);
      setEditedResult(null);
      setIsEditing(false);
      setPrimaryTermId(null);
      // Auto-select the term with highest confidence as primary
      if (selectedTerms.length > 0) {
        const bestTerm = selectedTerms.reduce((best, current) =>
          (current.confidence_score || 0) > (best.confidence_score || 0) ? current : best
        );
        setPrimaryTermId(bestTerm.id);
      }
    }
  }, [isOpen, selectedTerms.length]);

  // Generate preview whenever strategy or primary term changes
  useEffect(() => {
    if (isOpen && selectedTerms.length >= 2) {
      generatePreview();
    }
  }, [mergeStrategy, primaryTermId, isOpen, selectedTerms.length]);

  const generatePreview = useCallback(async () => {
    if (selectedTerms.length < 2) return;

    setIsGeneratingPreview(true);
    try {
      const response = await api.post(`/api/terminology/${projectId}/merge-preview`, {
        terms: selectedTerms,
        strategy: mergeStrategy,
        primaryTermId: mergeStrategy === 'manual' ? primaryTermId : undefined
      });

      if (response.data.success) {
        setPreview(response.data.data);
        setEditedResult(response.data.data.preview);
      } else {
        throw new Error(response.data.error || 'Failed to generate preview');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to generate merge preview');
      console.error('Preview generation error:', error);
    } finally {
      setIsGeneratingPreview(false);
    }
  }, [selectedTerms, mergeStrategy, primaryTermId, projectId]);

  const executeMerge = async () => {
    if (!preview || !editedResult) {
      toast.error('No merge preview available');
      return;
    }

    setIsMerging(true);
    try {
      const response = await api.post(`/api/terminology/${projectId}/merge`, {
        terms: selectedTerms,
        mergedResult: editedResult,
        strategy: preview.strategy_used
      });

      if (response.data.success) {
        toast.success(response.data.message);
        onMergeComplete();
        onClose();
      } else {
        throw new Error(response.data.error || 'Merge failed');
      }
    } catch (error: any) {
    // Get the specific error message from backend
    let errorMessage = 'Failed to execute merge';
    
    if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    // Handle duplicate name error specifically
    if (errorMessage.includes('already exists')) {
      toast.error(`${errorMessage} Please edit the term name in the preview above and try again.`, {
        duration: 6000, // Longer duration for this important message
      });
    } else {
      toast.error(errorMessage);
    }
    
    console.error('Merge execution error:', error);
  } finally {
      setIsMerging(false);
    }
  };

  const handleEditChange = (field: keyof MergedResult, value: string | number) => {
    if (editedResult) {
      setEditedResult({
        ...editedResult,
        [field]: value
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Merge Terminology ({selectedTerms.length} terms)
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isMerging}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          {/* Strategy Selection */}
          <div className="p-6 border-b bg-gray-50">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Merge Strategy</h3>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setMergeStrategy('ai')}
                disabled={isGeneratingPreview || isMerging}
                className={`p-4 rounded-lg border text-left transition-all ${mergeStrategy === 'ai'
                    ? 'border-blue-500 bg-blue-50 text-blue-900'
                    : 'border-gray-300 hover:border-gray-400'
                  }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <Zap className="w-5 h-5 text-blue-600" />
                  <span className="font-medium">AI-Powered Merge</span>
                </div>
                <p className="text-sm text-gray-600">
                  Let Claude analyze and intelligently combine the terms
                </p>
              </button>

              <button
                onClick={() => setMergeStrategy('manual')}
                disabled={isGeneratingPreview || isMerging}
                className={`p-4 rounded-lg border text-left transition-all ${mergeStrategy === 'manual'
                    ? 'border-green-500 bg-green-50 text-green-900'
                    : 'border-gray-300 hover:border-gray-400'
                  }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-5 h-5 text-green-600" />
                  <span className="font-medium">Manual Merge</span>
                </div>
                <p className="text-sm text-gray-600">
                  Choose a primary term and manually combine definitions
                </p>
              </button>
            </div>

            {/* Primary Term Selection for Manual Strategy */}
            {mergeStrategy === 'manual' && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Term (will be the base for merging)
                </label>
                <select
                  value={primaryTermId || ''}
                  onChange={(e) => setPrimaryTermId(parseInt(e.target.value))}
                  disabled={isGeneratingPreview || isMerging}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select primary term...</option>
                  {selectedTerms.map((term) => (
                    <option key={term.id} value={term.id}>
                      {term.name || term.term} (Confidence: {term.confidence_score || 0}%)
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Terms to Merge */}
          <div className="p-6 border-b">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Terms to Merge</h3>
            <div className="space-y-3">
              {selectedTerms.map((term, index) => (
                <div
                  key={term.id}
                  className={`p-3 rounded-lg border ${primaryTermId === term.id
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 bg-gray-50'
                    }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900">
                          {term.name || term.term}
                        </span>
                        {primaryTermId === term.id && (
                          <span className="px-2 py-1 text-xs bg-green-600 text-white rounded">
                            PRIMARY
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{term.definition}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>Confidence: {term.confidence_score || 0}%</span>
                        {term.category && <span>Category: {term.category}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Preview Section */}
          {isGeneratingPreview ? (
            <div className="p-6 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
              <p className="text-gray-600">Generating merge preview...</p>
            </div>
          ) : preview && editedResult ? (
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Merge Preview</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsEditing(!isEditing)}
                    disabled={isMerging}
                    className="flex items-center gap-2 px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  >
                    <Edit3 className="w-4 h-4" />
                    {isEditing ? 'Done Editing' : 'Edit Result'}
                  </button>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-blue-900">
                    {preview.strategy_used === 'ai' ? 'AI-Generated' : 'Manual'} Merge Result
                  </span>
                </div>

                {isEditing ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Term Name
                      </label>
                      <input
                        type="text"
                        value={editedResult.name}
                        onChange={(e) => handleEditChange('name', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Definition
                      </label>
                      <textarea
                        value={editedResult.definition}
                        onChange={(e) => handleEditChange('definition', e.target.value)}
                        rows={4}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Category
                        </label>
                        <input
                          type="text"
                          value={editedResult.category || ''}
                          onChange={(e) => handleEditChange('category', e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Confidence Score
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={editedResult.confidence_score}
                          onChange={(e) => handleEditChange('confidence_score', parseInt(e.target.value))}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <span className="font-semibold text-gray-900 text-lg">
                        {editedResult.name}
                      </span>
                    </div>
                    <p className="text-gray-700">{editedResult.definition}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span>Confidence: {editedResult.confidence_score}%</span>
                      {editedResult.category && <span>Category: {editedResult.category}</span>}
                    </div>
                  </div>
                )}
              </div>

              {/* Merge Reasoning */}
              {editedResult.merge_reasoning && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div>
                      <span className="font-medium text-yellow-900">Merge Reasoning</span>
                      <p className="text-sm text-yellow-800 mt-1">{editedResult.merge_reasoning}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Impact Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Impact Summary</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• {selectedTerms.length} terms will be merged into 1</li>
                  <li>• {preview.will_hide_terms.length} original terms will be hidden</li>
                  <li>• New term will appear in terminology list</li>
                  <li>• Merge can be viewed in history</li>
                </ul>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={isMerging}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={generatePreview}
              disabled={isGeneratingPreview || isMerging || selectedTerms.length < 2}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGeneratingPreview ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Generating...
                </>
              ) : (
                'Regenerate Preview'
              )}
            </button>

            <button
              onClick={executeMerge}
              disabled={!preview || isMerging || isGeneratingPreview}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isMerging ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Merging...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Execute Merge
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MergeTermsModal;